import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";

// Two-factor (TOTP) enrollment / management. Shared by Account and the Admin gate.
export const MfaPanel = ({ onEnabled }) => {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backup, setBackup] = useState(null);
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const load = () => api.get("/auth/mfa/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ enabled: false }));
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); }
    catch { toast.error("Could not start setup"); }
    setBusy(false);
  };
  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code: code.trim() });
      setBackup(data.backup_codes); setSetup(null); setCode(""); await load();
      toast.success("Two-factor enabled");
      if (onEnabled) onEnabled();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
    setBusy(false);
  };
  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password: disablePw, code: disableCode.trim() });
      setDisablePw(""); setDisableCode(""); setBackup(null); await load();
      toast.success("Two-factor disabled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not disable"); }
    setBusy(false);
  };

  return (
    <div className="mt-6 pt-6 border-t border-border" data-testid="mfa-panel">
      <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2">Two-Factor Authentication
        {status?.enabled && <span className="text-[10px] uppercase bg-emerald/15 text-emerald px-2 py-0.5 rounded-full" data-testid="mfa-on-badge">On</span>}
      </h3>
      <p className="text-xs text-muted-foreground mb-4">Add a second step at sign-in using an authenticator app (Google Authenticator, Authy). Required for admin accounts.</p>

      {backup && (
        <div className="rounded-xl border border-gold/40 bg-gold/10 p-4 mb-4" data-testid="mfa-backup-codes">
          <div className="font-semibold text-sm mb-2">Save your backup codes</div>
          <p className="text-xs text-muted-foreground mb-2">Each code works once if you lose your device. Store them somewhere safe — they won't be shown again.</p>
          <div className="grid grid-cols-2 gap-1.5 font-mono-sn text-sm">{backup.map((c) => <div key={c} className="bg-card rounded px-2 py-1 border border-border">{c}</div>)}</div>
        </div>
      )}

      {!status ? <Loader2 className="animate-spin text-emerald" size={20} /> : status.enabled ? (
        <div className="space-y-3 max-w-sm">
          <p className="text-sm text-emerald-deep">Two-factor is protecting this account. {status.backup_codes_left} backup code(s) left.</p>
          <input type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} placeholder="Your password" className="w-full rounded-xl border border-border px-4 py-3 text-sm" data-testid="mfa-disable-password" />
          <input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="Current 2FA / backup code" className="w-full rounded-xl border border-border px-4 py-3 text-sm" data-testid="mfa-disable-code" />
          <button onClick={disable} disabled={busy} className="border border-coral text-coral rounded-xl px-6 py-3 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="mfa-disable-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} Turn off two-factor
          </button>
        </div>
      ) : setup ? (
        <div className="space-y-3 max-w-sm">
          <p className="text-sm">1. Scan this QR code in your authenticator app:</p>
          <img src={setup.qr} alt="2FA QR code" className="h-44 w-44 rounded-lg border border-border bg-white" data-testid="mfa-qr" />
          <p className="text-xs text-muted-foreground break-all">Or enter this key manually: <span className="font-mono-sn">{setup.secret}</span></p>
          <p className="text-sm">2. Enter the 6-digit code it shows:</p>
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" className="w-full rounded-xl border border-border px-4 py-3 text-sm tracking-widest" data-testid="mfa-enable-code" />
          <button onClick={enable} disabled={busy} className="bg-emerald text-white rounded-xl px-6 py-3 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="mfa-enable-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Verify &amp; enable
          </button>
        </div>
      ) : (
        <button onClick={startSetup} disabled={busy} className="bg-ink text-white rounded-xl px-6 py-3 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="mfa-setup-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : null} Enable two-factor
        </button>
      )}
    </div>
  );
};
